import { useEffect } from "react";
import { useGlobalState } from "@/helpers/useGlobalState";
import { getGlobalContentitem } from "@/helpers/contentHelpers";
import { CookiePreferencesLink } from "@/components/CookieConsent";
import { Link, Outlet } from "react-router-dom";
import type { ImageProps } from "@/types/imageProps";
import { withLocale } from "@/helpers/withLocale";
import { topMenuItems, bottomMenuItems, renderMenuLabel } from "@/helpers/menuHelpers";
import MalencoNaw from "@/components/MalencoNaw";
import { useMsal } from "@azure/msal-react";
import { getActiveAccount, logout } from "@/auth/auth";
import { loginRequest } from "@/auth/msalConfig";
import { useIsAdmin } from "@/helpers/useIsAdmin";
import { useIsCustomerManager } from "@/helpers/useIsCustomerManager";

type LayoutProps = { titleCode?: string };

export default function Layout({ titleCode }: LayoutProps) {
    const { globalState } = useGlobalState();
    const { instance } = useMsal();

    const activeAccount = getActiveAccount(instance);
    const isAuthenticated = !!activeAccount;
    const isAdmin = useIsAdmin();
    const isCustomerManager = useIsCustomerManager();

    const pageTitle =
        titleCode
            ? getGlobalContentitem(titleCode, globalState)?.title
            : null;

    const logo: ImageProps = {
        url: "Malenco_Academy_schild.png",
        alt: getGlobalContentitem("MalencoLogoImg", globalState).title,
        title: getGlobalContentitem("MalencoLogoImg", globalState).content
    };

    useEffect(() => {
        try {
            const title = pageTitle ? `${pageTitle} | Malenco Academy` : "Malenco Academy";
            const description = getGlobalContentitem("OpenGraphPageDescription", globalState)?.content;
            const url = window.location.href;
            const image = `${window.location.origin}/media/img/` + logo.url;

            document.title = title;

            const setMeta = (property: string, content: string) => {
                let tag = document.querySelector(`meta[property='${property}']`) as HTMLMetaElement;
                if (!tag) {
                    tag = document.createElement("meta");
                    tag.setAttribute("property", property);
                    document.head.appendChild(tag);
                }
                tag.setAttribute("content", content);
            };

            setMeta("og:title", title);
            setMeta("og:description", description ?? "");
            setMeta("og:type", "website");
            setMeta("og:url", url);
            setMeta("og:image", image);
        } catch (err) {
            console.error("Error in LayoutNoHeader useEffect:", err);
        }
    }, [globalState.Logo?.url, globalState.Contentitems]);

    return (
        <div className="container">
            <div className="left"></div>
            <div className="center">
                <div className="PagebarSection">
                    <div className="PagebarLogoSection">
                        <img
                            src={"/media/img/" + logo.url}
                            alt={logo.alt}
                            title={logo.title}
                        />
                    </div>
                    <div className="PagebarNameSection">Malenco Academy</div>
                    <div className="PagebarTitleSection">
                        <h1>{pageTitle}</h1>
                    </div>
                    <header className={"TopMenuBar AcademyHeaderBg"}>
                        <nav className="TopMenuBarInner">
                            {topMenuItems
                                .filter(item => {
                                    // Filter out admin links if user is not admin
                                    if (item.to === "/admin/programs") {
                                        return isAuthenticated && isAdmin;
                                    }
                                    // Filter out manager links if user is not CustomerManager
                                    if (item.to.startsWith("/manager")) {
                                        return isAuthenticated && isCustomerManager;
                                    }
                                    return true;
                                })
                                .map((item) => (
                                    <Link key={item.to} to={withLocale(item.to, globalState.Language)} className={"TopMenuLink" + (location.pathname === item.to ? " Active" : "")} title={getGlobalContentitem(item.code, globalState).content}>
                                        {renderMenuLabel(item.code, item.fallback, globalState)}
                                    </Link>
                                ))
                            }
                            {isAuthenticated ? (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        logout(instance);
                                    }}
                                    href="#"
                                    className="TopMenuLink"
                                    style={{ cursor: "pointer" }}
                                    title={getGlobalContentitem("LogoutBtn", globalState)?.content ?? "Afmelden"}
                                >
                                    {getGlobalContentitem("LogoutBtn", globalState)?.title ?? "Afmelden"}
                                </a>
                            ) : (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        instance.loginRedirect(loginRequest);
                                    }}
                                    href="#"
                                    className="TopMenuLink"
                                    style={{ cursor: "pointer" }}
                                    title={getGlobalContentitem("LoginBtn", globalState)?.content ?? "Inloggen"}
                                >
                                    {getGlobalContentitem("LoginBtn", globalState)?.title ?? "Inloggen"}
                                </a>
                            )}
                        </nav>
                    </header>
                </div>

                <div>
                    <Outlet />
                </div>

                <div className="PagebarSection">
                    <div className="PagebarContentSection">
                        <MalencoNaw />
                    </div>
                    <div className="PagebarLinkSection">
                        {bottomMenuItems.map((item) => (
                            <Link key={item.to} to={withLocale(item.to, globalState.Language)}>{renderMenuLabel(item.code, item.fallback, globalState)}</Link>
                        ))}
                        <CookiePreferencesLink />
                    </div>
                </div>
            </div>
            <div className="right"></div>
        </div>
    );
}
